'use client';

import { useEffect, useMemo, useState } from 'react';
import { ExpertRow, Flex, Heading, Text } from '@highereducation/hen-ui';
import { contributorToExpertItem } from '@highereducation/features';
import {
  type ContributorCard,
  contributorHubSubtitle,
} from '@hen-site/types/contributor';

const PAGE_SIZE = 9;

/** Matches Tailwind `lg` in this app (see hen-ui tailwind `screens.lg`). */
const LG_MIN_WIDTH_QUERY = '(min-width: 1024px)';

const FILTER_TABS = [
  { filter: 'All' as const, label: 'All Contributors' },
  { filter: 'Editor' as const, label: 'Editors' },
  { filter: 'Reviewer' as const, label: 'Reviewers' },
  { filter: 'Writer' as const, label: 'Writers' },
];

type RoleFilter = (typeof FILTER_TABS)[number]['filter'];

interface ContributorsHubViewProps {
  contributors: ContributorCard[];
  /** Shown in default intro copy (e.g. site or brand name) */
  siteDisplayName?: string;
  /** When provided, replaces the default intro paragraph. */
  description?: string;
  showPolicy?: boolean;
}

export default function ContributorsHubView({
  contributors,
  siteDisplayName = 'this site',
  description,
  showPolicy,
}: ContributorsHubViewProps) {
  const [activeFilter, setActiveFilter] = useState<RoleFilter>('All');
  const [isLgViewport, setIsLgViewport] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia(LG_MIN_WIDTH_QUERY);
    setIsLgViewport(mql.matches);

    const onChange = (e: MediaQueryListEvent) => setIsLgViewport(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const filtered = useMemo(() => {
    if (activeFilter === 'All') return contributors;
    return contributors.filter((contributor) =>
      contributor.roles.some(
        (role) => role.toLowerCase() === activeFilter.toLowerCase(),
      ),
    );
  }, [contributors, activeFilter]);

  const experts = useMemo(
    () =>
      filtered.map((c) => ({
        ...contributorToExpertItem(c),
        occupation: contributorHubSubtitle(c),
      })),
    [filtered],
  );

  return (
    <Flex direction='column' gap='3xl' className='w-full'>
      <Flex
        direction='column'
        gap='xs'
        className='lg:items-center lg:text-center'
        asChild
      >
        <header>
          <Heading
            variant='h1'
            className='text-left text-heading lg:text-center'
          >
            Our Contributors
          </Heading>

          <Text
            variant='b3'
            color='default'
            className='text-left lg:text-center'
          >
            {description ??
              `Meet the team of contributing writers and reviewers at ${siteDisplayName}.`}
          </Text>

          {showPolicy && (
            <Text
              variant='b4'
              color='subtext'
              className='max-w-3xl text-left lg:text-center'
            >
              Our contributors are experts in their fields who follow our
              editorial guidelines to ensure accuracy and trustworthiness.
            </Text>
          )}
        </header>
      </Flex>

      <Flex direction='column' gap='xl'>
        <Flex
          direction='row'
          gap='lg'
          wrap='nowrap'
          className='overflow-x-auto scrollbar-none pb-px lg:justify-center lg:gap-xl'
          asChild
        >
          <nav aria-label='Filter contributors by role'>
            {FILTER_TABS.map(({ filter, label }) => {
              const selected = activeFilter === filter;
              return (
                <button
                  key={filter}
                  type='button'
                  aria-pressed={selected}
                  onClick={() => setActiveFilter(filter)}
                  className={[
                    'shrink-0 border-b-[3px] px-xxs pb-xs text-left text-sm font-semibold tracking-wide transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2',
                    selected
                      ? 'border-primary-700 text-heading'
                      : 'border-transparent text-neutral-600 hover:border-neutral-100 hover:text-heading',
                  ].join(' ')}
                >
                  {label}
                </button>
              );
            })}
          </nav>
        </Flex>

        {filtered.length === 0 ? (
          <Text
            variant='b3'
            color='subtext'
            className='text-left lg:text-center'
          >
            No contributors found.
          </Text>
        ) : (
          <ExpertRow
            experts={experts}
            size='sm'
            border={isLgViewport ? 'paddingOnly' : 'none'}
            gap='2xl'
            pageSize={PAGE_SIZE}
            headingContext='section'
          />
        )}
      </Flex>
    </Flex>
  );
}
